import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Menu from 'components/menu'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import {
  Calendar,
  Dependencies as DependenciesIcon,
  Drag,
  Folder,
  FolderOpen,
  FolderTree,
  FolderUp,
  Options,
  SettingsLine as SettingsIcon,
  SortAZ,
  Users
} from 'components/icons'
import SidebarMenuList, { MENU_SEPARATOR, SidebarMenuEntry, SidebarMenuItem } from 'components/sidebar/menuItems'
import { SIDEBAR_ACTION } from 'components/sidebar/surface'
import { belowButton } from 'components/sidebar/rowButton'
import { SIDEBAR_SORTS, SORT_LABEL, SidebarSort } from 'components/sidebar/components/folders/tree'

const MENU_WIDTH = 220

/** Each sort's icon: the drag grip for the order people arrange by hand, A→Z, and the date icon. */
const SORT_ICON: Record<SidebarSort, React.ReactNode> = {
  manual: <Drag />,
  name: <SortAZ />,
  created: <Calendar />
}

interface HubOptionsMenuProps {
  /** The hub's Relationships page, for hub Owners; undefined hides the entry. */
  relationshipsPath?: string
  /** The Members section of the hub's settings, for hub Owners; undefined hides the entry. */
  membersPath?: string
  /** The hub's settings, for hub Owners and Builders; undefined hides the entry. */
  settingsPath?: string
  sort: SidebarSort
  onSortChange: (sort: SidebarSort) => void
  /** The viewer lists each level's folders before its documents and tables. */
  foldersFirst: boolean
  onFoldersFirstChange: (foldersFirst: boolean) => void
  /** Whether the hub has folders; with none, there's nothing to expand or collapse. */
  hasFolders: boolean
  expandAll: () => void
  collapseAll: () => void
}

/**
 * The ⋯ at the right of the hub's name in the sidebar: Members and Hub settings; under Organise, the
 * viewer's own display choices (Expand all, Collapse all, Sort files); then Relationships. The button
 * itself never changes with the sort: the ticked choice under Sort files says which is on.
 */
const HubOptionsMenu: React.FC<HubOptionsMenuProps> = ({
  relationshipsPath,
  membersPath,
  settingsPath,
  sort,
  onSortChange,
  foldersFirst,
  onFoldersFirstChange,
  hasFolders,
  expandAll,
  collapseAll
}) => {
  const navigate = useNavigate()
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  // Holds the button: the Tooltip around it takes the button's own ref.
  const holder = useRef<HTMLDivElement>(null)

  const close = (): void => {
    setAt(null)
    holder.current?.querySelector('button')?.focus({ preventScroll: true })
  }

  /** Closes the menu, then does `action`. */
  const choose = (action: () => void) => () => {
    close()
    action()
  }

  const sortItems: SidebarMenuEntry[] = [
    ...SIDEBAR_SORTS.map((option) => ({
      testId: `sidebar-sort-${option}`,
      icon: SORT_ICON[option],
      label: SORT_LABEL[option],
      checked: option === sort,
      onSelect: choose(() => onSortChange(option))
    })),
    MENU_SEPARATOR,
    {
      testId: 'sidebar-folders-first',
      icon: <FolderUp />,
      label: 'Folders first',
      checked: foldersFirst,
      toggle: true,
      onSelect: choose(() => onFoldersFirstChange(!foldersFirst))
    }
  ]

  /** An entry that goes to `path`; none when the viewer can't open it. */
  const link = (path: string | undefined, entry: Omit<SidebarMenuItem, 'onSelect' | 'submenu'>): SidebarMenuItem[] =>
    path ? [{ ...entry, onSelect: choose(() => navigate(path)) }] : []

  const items: SidebarMenuEntry[] = [
    ...link(membersPath, { testId: 'hub-members-link', icon: <Users />, label: 'Members' }),
    ...link(settingsPath, {
      testId: 'hub-settings-link',
      dataTour: 'hub-settings-link',
      icon: <SettingsIcon />,
      label: 'Hub settings'
    }),
    MENU_SEPARATOR,
    { heading: 'Organise' },
    ...(hasFolders
      ? [
          {
            testId: 'sidebar-expand-all',
            icon: <FolderOpen />,
            label: 'Expand all',
            onSelect: choose(expandAll)
          },
          {
            testId: 'sidebar-collapse-all',
            icon: <Folder />,
            label: 'Collapse all',
            onSelect: choose(collapseAll)
          }
        ]
      : []),
    { testId: 'sidebar-sort', icon: <FolderTree />, label: 'Sort files', submenu: sortItems },
    MENU_SEPARATOR,
    ...link(relationshipsPath, { testId: 'hub-dependencies-link', icon: <DependenciesIcon />, label: 'Relationships' })
  ]

  return (
    <div ref={holder} className="flex items-center shrink-0">
      {/* Hidden while the menu is open: it would sit over the menu's first row. */}
      <Tooltip content="More actions" visible={at !== null ? false : undefined}>
        <Button
          data-testid="sidebar-hub-options"
          data-tour="hub-options"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={at !== null}
          className={SIDEBAR_ACTION}
          internalType="transparent"
          onClick={(event) => setAt(belowButton(event.currentTarget))}
        >
          <Options />
        </Button>
      </Tooltip>
      {at && (
        <Menu
          id="sidebar-hub-options-menu"
          menuState={{ open: true, top: `${at.top}px`, left: `${at.left}px`, right: 'auto', bottom: 'auto' }}
          setMenuState={(state) => {
            if (!state.open) close()
          }}
          width={MENU_WIDTH}
          fixed
        >
          <SidebarMenuList label="More actions" items={items} onClose={close} />
        </Menu>
      )}
    </div>
  )
}

export default HubOptionsMenu
